import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import { isAnalyticsExportFullPage } from '../../constants/analyticsExportDomains';

function normalizeExportRange(range) {
  if (!range) return '7d';
  if (range === '365d') return '1y';
  return range;
}

export const createAnalyticsExport = createAsyncThunk(
  'analyticsExport/createAnalyticsExport',
  async (
    {
      domain,
      section,
      currentRange,
      startDate = null,
      endDate = null,
      timezone,
      group_id,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const businessId = loginToken?.response?.user?.business_id;

      if (!businessId) {
        return rejectWithValue({ message: 'Missing business id' });
      }
      const isFullPageExport = isAnalyticsExportFullPage(domain, section);
      if (!domain || !section) {
        return rejectWithValue({ message: 'Missing export domain or section' });
      }

      const apiRange = normalizeExportRange(currentRange);
      const tz =
        timezone ||
        (typeof Intl !== 'undefined' &&
          Intl.DateTimeFormat?.().resolvedOptions?.().timeZone) ||
        'UTC';

      /** Full-page CSV (any domain): API accepts only `range` + `timezone`. */
      const body = isFullPageExport
        ? { range: apiRange, timezone: tz }
        : {
            scope: 'section',
            section,
            range: apiRange,
            timezone: tz,
          };

      if (!isFullPageExport && apiRange === 'custom' && startDate && endDate) {
        body.from = startDate;
        body.to = endDate;
      }

      if (group_id) {
        body.group_id = group_id;
      }

      const response = await axios.post(
        `${ROOT_URL}/analytics/exports/${domain}/${businessId}`,
        body,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      const data = error.response?.data;
      const message =
        (typeof data === 'object' && data?.message) ||
        data?.error ||
        error.message ||
        'Export failed';
      return rejectWithValue(typeof message === 'string' ? { message } : data);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  lastResult: null,
};

const analyticsExportSlice = createSlice({
  name: 'analyticsExport',
  initialState,
  reducers: {
    clearAnalyticsExport: (state) => {
      state.error = null;
      state.lastResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAnalyticsExport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAnalyticsExport.fulfilled, (state, action) => {
        state.loading = false;
        state.lastResult = action.payload;
        state.error = null;
      })
      .addCase(createAnalyticsExport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? null;
        state.lastResult = null;
      });
  },
});

export const { clearAnalyticsExport } = analyticsExportSlice.actions;
export default analyticsExportSlice.reducer;
